/**
 * Invertible master FX / slot FX / slot mix patches (Phase 3).
 */
import type { FxNode, SlotMix } from "../../../../src/lib/audio";
import type { AdamCommand, AdamCommandContext, AdamCommandResult } from "./Command";

function requireProject(context: AdamCommandContext) {
  const project = context.project;
  if (!project) {
    throw new Error("Adam project bridge is not registered");
  }
  return project;
}

function cloneFx(effects: FxNode[]): FxNode[] {
  return structuredClone(effects);
}

function cloneMix(mix: SlotMix): SlotMix {
  return { volume: mix.volume, pan: mix.pan };
}

/** Stable topology key — param scrub merges; add/remove/reorder does not. */
function fxTopologyKey(effects: FxNode[]): string {
  return effects.map(fx => `${fx.id ?? ""}:${fx.type}`).join("|");
}

function isParamOnlyEdit(from: FxNode[], to: FxNode[]): boolean {
  return fxTopologyKey(from) === fxTopologyKey(to);
}

function fxEqual(a: FxNode[], b: FxNode[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function mixEqual(a: SlotMix, b: SlotMix): boolean {
  return a.volume === b.volume && a.pan === b.pan;
}

/** Replace the entire master FX strip. */
export class SetMasterFxCommand implements AdamCommand {
  readonly id = "setMasterFx";
  readonly label = "Master FX";

  constructor(
    readonly from: FxNode[],
    readonly to: FxNode[],
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setMasterFx(cloneFx(this.to));
    return { changed: "fx" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setMasterFx(cloneFx(this.from));
    return { changed: "fx" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return (
      command instanceof SetMasterFxCommand &&
      isParamOnlyEdit(this.from, this.to) &&
      isParamOnlyEdit(command.from, command.to) &&
      fxTopologyKey(this.to) === fxTopologyKey(command.from)
    );
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetMasterFxCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetMasterFxCommand(cloneFx(this.from), cloneFx(command.to));
  }
}

/** Replace one track's insert FX stack. */
export class SetSlotFxCommand implements AdamCommand {
  readonly id = "setSlotFx";
  readonly label = "Track FX";

  constructor(
    readonly slot: number,
    readonly from: FxNode[],
    readonly to: FxNode[],
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setSlotFx(this.slot, cloneFx(this.to));
    return { changed: "slotFx" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setSlotFx(this.slot, cloneFx(this.from));
    return { changed: "slotFx" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return (
      command instanceof SetSlotFxCommand &&
      command.slot === this.slot &&
      isParamOnlyEdit(this.from, this.to) &&
      isParamOnlyEdit(command.from, command.to) &&
      fxTopologyKey(this.to) === fxTopologyKey(command.from)
    );
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetSlotFxCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetSlotFxCommand(this.slot, cloneFx(this.from), cloneFx(command.to));
  }
}

/** Set one slot's volume/pan (scrub-mergeable). */
export class SetSlotMixCommand implements AdamCommand {
  readonly id = "setSlotMix";
  readonly label = "Slot mix";

  constructor(
    readonly slot: number,
    readonly from: SlotMix,
    readonly to: SlotMix,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setSlotMix(this.slot, cloneMix(this.to));
    return { changed: "slotMix" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireProject(context).setSlotMix(this.slot, cloneMix(this.from));
    return { changed: "slotMix" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof SetSlotMixCommand && command.slot === this.slot;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetSlotMixCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetSlotMixCommand(this.slot, cloneMix(this.from), cloneMix(command.to));
  }
}

export { fxEqual, mixEqual };
